import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { PageHeader, SearchInput, StatCard, StatsGrid } from '../components/ui';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  Key,
  Network,
  Pencil,
  Plus,
  ShieldCheck,
  Webhook,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── Endpoint map ──────────────────────────────────────────────────────────────

type Endpoint = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  scope: string;
  desc: string; // fallback (cs) — zobrazuje se přes t(descKey, desc)
  descKey: string;
};

type ModuleDef = {
  name: string;
  desc: string; // fallback (cs) — zobrazuje se přes t(descKey, desc)
  descKey: string;
  color: string;
  readScope: string;
  writeScope: string;
  baseUrl: string;
  endpoints: Endpoint[];
};

const MODULES: ModuleDef[] = [
  {
    name: 'ocr',
    desc: 'OCR pipeline — GOT + Qwen2-VL',
    descKey: 'apiMap.modules.ocr.desc',
    color: 'purple',
    readScope: 'ocr:read',
    writeScope: 'ocr:write',
    baseUrl: '/apiext/ocr',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        scope: 'ocr:read',
        desc: 'Stav workerů (GOT + Qwen)',
        descKey: 'apiMap.modules.ocr.endpoints.status',
      },
      {
        method: 'GET',
        path: '/config',
        scope: 'ocr:read',
        desc: 'Konfigurace modulu',
        descKey: 'apiMap.modules.ocr.endpoints.configGet',
      },
      {
        method: 'PUT',
        path: '/config',
        scope: 'ocr:write',
        desc: 'Změna konfigurace',
        descKey: 'apiMap.modules.ocr.endpoints.configPut',
      },
      {
        method: 'POST',
        path: '/',
        scope: 'ocr:write',
        desc: 'OCR jednoho souboru (FormData: file, mode)',
        descKey: 'apiMap.modules.ocr.endpoints.single',
      },
      {
        method: 'POST',
        path: '/batch',
        scope: 'ocr:write',
        desc: 'OCR dávky souborů',
        descKey: 'apiMap.modules.ocr.endpoints.batch',
      },
      {
        method: 'POST',
        path: '/job',
        scope: 'ocr:write',
        desc: 'Odeslat asynchronní job',
        descKey: 'apiMap.modules.ocr.endpoints.jobSubmit',
      },
      {
        method: 'GET',
        path: '/job/:id',
        scope: 'ocr:read',
        desc: 'Stav jobu',
        descKey: 'apiMap.modules.ocr.endpoints.jobStatus',
      },
      {
        method: 'GET',
        path: '/logs',
        scope: 'ocr:read',
        desc: 'Logy workeru',
        descKey: 'apiMap.modules.ocr.endpoints.logs',
      },
      {
        method: 'POST',
        path: '/manage',
        scope: 'ocr:write',
        desc: 'Spustit/zastavit/restartovat worker',
        descKey: 'apiMap.modules.ocr.endpoints.manage',
      },
      {
        method: 'GET',
        path: '/got/status',
        scope: 'ocr:read',
        desc: 'Stav GOT kontejneru',
        descKey: 'apiMap.modules.ocr.endpoints.gotStatus',
      },
      {
        method: 'POST',
        path: '/got/deploy',
        scope: 'ocr:write',
        desc: 'Nasadit GOT kontejner',
        descKey: 'apiMap.modules.ocr.endpoints.gotDeploy',
      },
      {
        method: 'POST',
        path: '/got/manage',
        scope: 'ocr:write',
        desc: 'GOT start/stop/restart/remove',
        descKey: 'apiMap.modules.ocr.endpoints.gotManage',
      },
      {
        method: 'GET',
        path: '/pipeline',
        scope: 'ocr:read',
        desc: 'Seznam pipeline jobů',
        descKey: 'apiMap.modules.ocr.endpoints.pipelineList',
      },
      {
        method: 'GET',
        path: '/pipeline/:key',
        scope: 'ocr:read',
        desc: 'Detail pipeline jobu',
        descKey: 'apiMap.modules.ocr.endpoints.pipelineDetail',
      },
      {
        method: 'POST',
        path: '/pipeline',
        scope: 'webhook',
        desc: 'Supabase Storage webhook (x-webhook-secret)',
        descKey: 'apiMap.modules.ocr.endpoints.pipelineWebhook',
      },
    ],
  },
  {
    name: 'fleet',
    desc: 'Fleet agenti — enrollmenty, příkazy, heartbeat',
    descKey: 'apiMap.modules.fleet.desc',
    color: 'blue',
    readScope: 'fleet:read',
    writeScope: 'fleet:write',
    baseUrl: '/apiext/fleet',
    endpoints: [
      {
        method: 'GET',
        path: '/agents',
        scope: 'fleet:read',
        desc: 'Seznam agentů',
        descKey: 'apiMap.modules.fleet.endpoints.agentsList',
      },
      {
        method: 'GET',
        path: '/agents/:id',
        scope: 'fleet:read',
        desc: 'Detail agenta',
        descKey: 'apiMap.modules.fleet.endpoints.agentDetail',
      },
      {
        method: 'POST',
        path: '/agents/:id/command',
        scope: 'fleet:write',
        desc: 'Příkaz agentovi',
        descKey: 'apiMap.modules.fleet.endpoints.agentCommand',
      },
      {
        method: 'DELETE',
        path: '/agents/:id',
        scope: 'fleet:write',
        desc: 'Smazat agenta',
        descKey: 'apiMap.modules.fleet.endpoints.agentDelete',
      },
      {
        method: 'POST',
        path: '/enroll',
        scope: 'fleet:write',
        desc: 'Enrollment nového agenta',
        descKey: 'apiMap.modules.fleet.endpoints.enroll',
      },
    ],
  },
  {
    name: 'supabase',
    desc: 'Správa Supabase instancí',
    descKey: 'apiMap.modules.supabase.desc',
    color: 'green',
    readScope: 'supabase:read',
    writeScope: 'supabase:write',
    baseUrl: '/apiext/supabase',
    endpoints: [
      {
        method: 'GET',
        path: '/instances',
        scope: 'supabase:read',
        desc: 'Seznam instancí s porty a statusem',
        descKey: 'apiMap.modules.supabase.endpoints.instancesList',
      },
      {
        method: 'POST',
        path: '/instances',
        scope: 'supabase:write',
        desc: 'Deploy nové instance',
        descKey: 'apiMap.modules.supabase.endpoints.instanceCreate',
      },
      {
        method: 'DELETE',
        path: '/instances/:name',
        scope: 'supabase:write',
        desc: 'Smazat instanci',
        descKey: 'apiMap.modules.supabase.endpoints.instanceDelete',
      },
      {
        method: 'POST',
        path: '/instances/:name/start',
        scope: 'supabase:write',
        desc: 'Spustit',
        descKey: 'apiMap.modules.supabase.endpoints.instanceStart',
      },
      {
        method: 'POST',
        path: '/instances/:name/stop',
        scope: 'supabase:write',
        desc: 'Zastavit',
        descKey: 'apiMap.modules.supabase.endpoints.instanceStop',
      },
      {
        method: 'POST',
        path: '/instances/:name/publish',
        scope: 'supabase:write',
        desc: 'Zveřejnit API/Studio (NPM + LE cert)',
        descKey: 'apiMap.modules.supabase.endpoints.instancePublish',
      },
      {
        method: 'POST',
        path: '/instances/:name/unpublish',
        scope: 'supabase:write',
        desc: 'Odebrat z veřejného přístupu',
        descKey: 'apiMap.modules.supabase.endpoints.instanceUnpublish',
      },
    ],
  },
  {
    name: 'npm',
    desc: 'NGINX Proxy Manager — proxy hosty a certifikáty',
    descKey: 'apiMap.modules.npm.desc',
    color: 'orange',
    readScope: 'npm:read',
    writeScope: 'npm:write',
    baseUrl: '/apiext/npm',
    endpoints: [
      {
        method: 'GET',
        path: '/hosts',
        scope: 'npm:read',
        desc: 'Seznam proxy hostů',
        descKey: 'apiMap.modules.npm.endpoints.hostsList',
      },
      {
        method: 'POST',
        path: '/hosts',
        scope: 'npm:write',
        desc: 'Nový proxy host (+ auto LE cert)',
        descKey: 'apiMap.modules.npm.endpoints.hostCreate',
      },
      {
        method: 'PUT',
        path: '/hosts/:id',
        scope: 'npm:write',
        desc: 'Upravit host',
        descKey: 'apiMap.modules.npm.endpoints.hostUpdate',
      },
      {
        method: 'DELETE',
        path: '/hosts/:id',
        scope: 'npm:write',
        desc: 'Smazat host',
        descKey: 'apiMap.modules.npm.endpoints.hostDelete',
      },
      {
        method: 'POST',
        path: '/hosts/:id/enable',
        scope: 'npm:write',
        desc: 'Zapnout host',
        descKey: 'apiMap.modules.npm.endpoints.hostEnable',
      },
      {
        method: 'POST',
        path: '/hosts/:id/disable',
        scope: 'npm:write',
        desc: 'Vypnout host',
        descKey: 'apiMap.modules.npm.endpoints.hostDisable',
      },
      {
        method: 'GET',
        path: '/certs',
        scope: 'npm:read',
        desc: 'Seznam certifikátů',
        descKey: 'apiMap.modules.npm.endpoints.certsList',
      },
    ],
  },
  {
    name: 'wiki',
    desc: 'Wiki záznamy (Markdown, MCP)',
    descKey: 'apiMap.modules.wiki.desc',
    color: 'teal',
    readScope: 'wiki:read',
    writeScope: 'wiki:write',
    baseUrl: '/apiext/wiki',
    endpoints: [
      {
        method: 'GET',
        path: '/entries',
        scope: 'wiki:read',
        desc: 'Výpis záznamu',
        descKey: 'apiMap.modules.wiki.endpoints.entriesList',
      },
      {
        method: 'GET',
        path: '/entries/:slug',
        scope: 'wiki:read',
        desc: 'Detail záznamu',
        descKey: 'apiMap.modules.wiki.endpoints.entryDetail',
      },
      {
        method: 'GET',
        path: '/search',
        scope: 'wiki:read',
        desc: 'Fulltextové hledání',
        descKey: 'apiMap.modules.wiki.endpoints.search',
      },
      {
        method: 'POST',
        path: '/entries',
        scope: 'wiki:write',
        desc: 'Vytvořit záznam',
        descKey: 'apiMap.modules.wiki.endpoints.entryCreate',
      },
      {
        method: 'PUT',
        path: '/entries/:slug',
        scope: 'wiki:write',
        desc: 'Upravit záznam',
        descKey: 'apiMap.modules.wiki.endpoints.entryUpdate',
      },
      {
        method: 'DELETE',
        path: '/entries/:slug',
        scope: 'wiki:write',
        desc: 'Smazat záznam',
        descKey: 'apiMap.modules.wiki.endpoints.entryDelete',
      },
    ],
  },
  {
    name: 'logy',
    desc: 'Audit log — Loki push + query',
    descKey: 'apiMap.modules.logy.desc',
    color: 'red',
    readScope: 'logy:read',
    writeScope: 'logy:write',
    baseUrl: '/loki/api/v1',
    endpoints: [
      {
        method: 'POST',
        path: '/push',
        scope: 'logy:write',
        desc: 'Push logů (Grafana Alloy / Promtail)',
        descKey: 'apiMap.modules.logy.endpoints.push',
      },
      {
        method: 'GET',
        path: '/api/log/entries',
        scope: 'logy:read',
        desc: 'Výpis log záznamů',
        descKey: 'apiMap.modules.logy.endpoints.entries',
      },
      {
        method: 'GET',
        path: '/api/log/stats',
        scope: 'logy:read',
        desc: 'Statistiky logů',
        descKey: 'apiMap.modules.logy.endpoints.stats',
      },
    ],
  },
  {
    name: 'eduroam',
    desc: 'FreeRADIUS / eduroam federace',
    descKey: 'apiMap.modules.eduroam.desc',
    color: 'yellow',
    readScope: 'eduroam:read',
    writeScope: 'eduroam:write',
    baseUrl: '/apiext/eduroam',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        scope: 'eduroam:read',
        desc: 'Stav FreeRADIUS',
        descKey: 'apiMap.modules.eduroam.endpoints.status',
      },
      {
        method: 'GET',
        path: '/config',
        scope: 'eduroam:read',
        desc: 'Konfigurace',
        descKey: 'apiMap.modules.eduroam.endpoints.configGet',
      },
      {
        method: 'PUT',
        path: '/config',
        scope: 'eduroam:write',
        desc: 'Změnit konfiguraci',
        descKey: 'apiMap.modules.eduroam.endpoints.configPut',
      },
      {
        method: 'POST',
        path: '/apply',
        scope: 'eduroam:write',
        desc: 'Aplikovat konfiguraci',
        descKey: 'apiMap.modules.eduroam.endpoints.apply',
      },
    ],
  },
  {
    name: 'pcc',
    desc: 'Project Control Center — projekty, CI/CD, proxy, DB',
    descKey: 'apiMap.modules.pcc.desc',
    color: 'teal',
    readScope: 'pcc:read',
    writeScope: 'pcc:write',
    baseUrl: '/apiext/pcc',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        scope: 'pcc:read',
        desc: 'Stav PCC kontejneru a API health',
        descKey: 'apiMap.modules.pcc.endpoints.status',
      },
    ],
  },
  {
    name: 'apphost',
    desc: 'Application Host — deploy Laravel, React, Node.js v Docker kontejnerech',
    descKey: 'apiMap.modules.apphost.desc',
    color: 'teal',
    readScope: 'apphost:read',
    writeScope: 'apphost:write',
    baseUrl: '/apiext/apphost',
    endpoints: [
      {
        method: 'GET',
        path: '/instances',
        scope: 'apphost:read',
        desc: 'Seznam instancí s live Docker statusem',
        descKey: 'apiMap.modules.apphost.endpoints.instancesList',
      },
      {
        method: 'POST',
        path: '/instances',
        scope: 'apphost:write',
        desc: 'Vytvořit instanci (wizard: name, type, db, redis, nginx, git)',
        descKey: 'apiMap.modules.apphost.endpoints.instanceCreate',
      },
      {
        method: 'GET',
        path: '/instances/:name',
        scope: 'apphost:read',
        desc: 'Detail instance — kontejnery, porty, git',
        descKey: 'apiMap.modules.apphost.endpoints.instanceDetail',
      },
      {
        method: 'DELETE',
        path: '/instances/:name',
        scope: 'apphost:write',
        desc: 'Smazat instanci (keep_data=true zachová volumes)',
        descKey: 'apiMap.modules.apphost.endpoints.instanceDelete',
      },
      {
        method: 'POST',
        path: '/instances/:name/start',
        scope: 'apphost:write',
        desc: 'Spustit kontejnery instance',
        descKey: 'apiMap.modules.apphost.endpoints.instanceStart',
      },
      {
        method: 'POST',
        path: '/instances/:name/stop',
        scope: 'apphost:write',
        desc: 'Zastavit kontejnery instance',
        descKey: 'apiMap.modules.apphost.endpoints.instanceStop',
      },
      {
        method: 'POST',
        path: '/instances/:name/restart',
        scope: 'apphost:write',
        desc: 'Restartovat app kontejner',
        descKey: 'apiMap.modules.apphost.endpoints.instanceRestart',
      },
      {
        method: 'POST',
        path: '/instances/:name/rebuild',
        scope: 'apphost:write',
        desc: 'Smazat a znovu spustit app kontejner',
        descKey: 'apiMap.modules.apphost.endpoints.instanceRebuild',
      },
      {
        method: 'POST',
        path: '/instances/:name/deploy',
        scope: 'apphost:write',
        desc: 'Deploy z Gitu (composer, migrate, restart)',
        descKey: 'apiMap.modules.apphost.endpoints.instanceDeploy',
      },
      {
        method: 'GET',
        path: '/instances/:name/config',
        scope: 'apphost:read',
        desc: 'Načíst .env proměnné',
        descKey: 'apiMap.modules.apphost.endpoints.configGet',
      },
      {
        method: 'PATCH',
        path: '/instances/:name/config',
        scope: 'apphost:write',
        desc: 'Nastavit / smazat .env proměnné',
        descKey: 'apiMap.modules.apphost.endpoints.configSet',
      },
      {
        method: 'GET',
        path: '/instances/:name/config/export',
        scope: 'apphost:write',
        desc: 'Stáhnout .env soubor',
        descKey: 'apiMap.modules.apphost.endpoints.configExport',
      },
      {
        method: 'POST',
        path: '/instances/:name/config/import',
        scope: 'apphost:write',
        desc: 'Nahrát .env soubor',
        descKey: 'apiMap.modules.apphost.endpoints.configImport',
      },
      {
        method: 'GET',
        path: '/instances/:name/logs',
        scope: 'apphost:read',
        desc: 'Docker logy (service=app|redis|db, lines=N)',
        descKey: 'apiMap.modules.apphost.endpoints.logs',
      },
      {
        method: 'POST',
        path: '/instances/:name/exec',
        scope: 'apphost:write',
        desc: 'Spustit příkaz v kontejneru (artisan, npm, …)',
        descKey: 'apiMap.modules.apphost.endpoints.exec',
      },
    ],
  },
  {
    name: 'stalwart',
    desc: 'Stalwart Mail Server — účty, domény, fronta, odesílání',
    descKey: 'apiMap.modules.stalwart.desc',
    color: 'blue',
    readScope: 'stalwart:read',
    writeScope: 'stalwart:write',
    baseUrl: '/apiext/stalwart',
    endpoints: [
      {
        method: 'GET',
        path: '/health',
        scope: 'stalwart:read',
        desc: 'Health + verze serveru',
        descKey: 'apiMap.modules.stalwart.endpoints.health',
      },
      {
        method: 'GET',
        path: '/accounts',
        scope: 'stalwart:read',
        desc: 'Seznam poštovních účtů',
        descKey: 'apiMap.modules.stalwart.endpoints.accountsList',
      },
      {
        method: 'POST',
        path: '/accounts',
        scope: 'stalwart:write',
        desc: 'Vytvořit účet',
        descKey: 'apiMap.modules.stalwart.endpoints.accountCreate',
      },
      {
        method: 'PATCH',
        path: '/accounts/:name',
        scope: 'stalwart:write',
        desc: 'Změnit heslo, kvótu, emaily',
        descKey: 'apiMap.modules.stalwart.endpoints.accountUpdate',
      },
      {
        method: 'DELETE',
        path: '/accounts/:name',
        scope: 'stalwart:write',
        desc: 'Smazat účet',
        descKey: 'apiMap.modules.stalwart.endpoints.accountDelete',
      },
      {
        method: 'GET',
        path: '/domains',
        scope: 'stalwart:read',
        desc: 'Seznam domén',
        descKey: 'apiMap.modules.stalwart.endpoints.domainsList',
      },
      {
        method: 'POST',
        path: '/domains',
        scope: 'stalwart:write',
        desc: 'Přidat doménu',
        descKey: 'apiMap.modules.stalwart.endpoints.domainCreate',
      },
      {
        method: 'DELETE',
        path: '/domains/:name',
        scope: 'stalwart:write',
        desc: 'Odebrat doménu',
        descKey: 'apiMap.modules.stalwart.endpoints.domainDelete',
      },
      {
        method: 'GET',
        path: '/queue',
        scope: 'stalwart:read',
        desc: 'SMTP fronta — zprávy čekající na doručení',
        descKey: 'apiMap.modules.stalwart.endpoints.queueList',
      },
      {
        method: 'DELETE',
        path: '/queue/:id',
        scope: 'stalwart:write',
        desc: 'Zrušit zprávu ve frontě',
        descKey: 'apiMap.modules.stalwart.endpoints.queueCancel',
      },
      {
        method: 'POST',
        path: '/queue/:id/retry',
        scope: 'stalwart:write',
        desc: 'Zkusit doručení znovu',
        descKey: 'apiMap.modules.stalwart.endpoints.queueRetry',
      },
      {
        method: 'POST',
        path: '/send',
        scope: 'stalwart:write',
        desc: 'Odeslat email (from, to, subject, text/html)',
        descKey: 'apiMap.modules.stalwart.endpoints.send',
      },
    ],
  },
  {
    name: 'backup',
    desc: 'Zálohy a snapshoty',
    descKey: 'apiMap.modules.backup.desc',
    color: 'slate',
    readScope: 'backup:read',
    writeScope: 'backup:write',
    baseUrl: '/apiext/backup',
    endpoints: [
      {
        method: 'GET',
        path: '/jobs',
        scope: 'backup:read',
        desc: 'Seznam zálohovacích jobů',
        descKey: 'apiMap.modules.backup.endpoints.jobsList',
      },
      {
        method: 'POST',
        path: '/jobs',
        scope: 'backup:write',
        desc: 'Nový zálohovací job',
        descKey: 'apiMap.modules.backup.endpoints.jobCreate',
      },
      {
        method: 'POST',
        path: '/jobs/:id/run',
        scope: 'backup:write',
        desc: 'Spustit zálohu ručně',
        descKey: 'apiMap.modules.backup.endpoints.jobRun',
      },
    ],
  },
];

// ── Color helpers ─────────────────────────────────────────────────────────────

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  POST: 'bg-green-500/15 text-green-400 border-green-500/20',
  PUT: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  PATCH: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  DELETE: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const MODULE_COLORS: Record<string, { border: string; bg: string; text: string; badge: string }> = {
  purple: {
    border: 'border-purple-500/20',
    bg: 'bg-purple-500/5',
    text: 'text-purple-400',
    badge: 'bg-purple-500/15 text-purple-400',
  },
  blue: {
    border: 'border-blue-500/20',
    bg: 'bg-blue-500/5',
    text: 'text-blue-400',
    badge: 'bg-blue-500/15 text-blue-400',
  },
  green: {
    border: 'border-green-500/20',
    bg: 'bg-green-500/5',
    text: 'text-green-400',
    badge: 'bg-green-500/15 text-green-400',
  },
  orange: {
    border: 'border-orange-500/20',
    bg: 'bg-orange-500/5',
    text: 'text-orange-400',
    badge: 'bg-orange-500/15 text-orange-400',
  },
  teal: {
    border: 'border-teal-500/20',
    bg: 'bg-teal-500/5',
    text: 'text-teal-400',
    badge: 'bg-teal-500/15 text-teal-400',
  },
  red: {
    border: 'border-red-500/20',
    bg: 'bg-red-500/5',
    text: 'text-red-400',
    badge: 'bg-red-500/15 text-red-400',
  },
  yellow: {
    border: 'border-yellow-500/20',
    bg: 'bg-yellow-500/5',
    text: 'text-yellow-400',
    badge: 'bg-yellow-500/15 text-yellow-400',
  },
  slate: {
    border: 'border-slate-500/20',
    bg: 'bg-slate-500/5',
    text: 'text-slate-400',
    badge: 'bg-slate-500/15 text-slate-400',
  },
};

// ── Key count badge ──────────────────────────────────────────────────────────

interface KeyEntry {
  id: string;
  label: string;
  scopes: string[];
  permissions: string[];
  enabled: boolean;
}
interface Namespace {
  namespace: string;
  keys: KeyEntry[];
}

function getScopes(k: KeyEntry) {
  const scopes = Array.isArray(k?.scopes) ? k.scopes.map(String).filter(Boolean) : [];
  if (scopes.length) return scopes;
  const perms = Array.isArray(k?.permissions) ? k.permissions : [];
  return perms.includes('write') ? ['*:read', '*:write'] : ['*:read'];
}

function keysForModule(namespaces: Namespace[], mod: string): KeyEntry[] {
  if (!Array.isArray(namespaces)) return [];
  return namespaces.flatMap((ns) => {
    const keys = Array.isArray(ns?.keys) ? ns.keys : [];
    return keys.filter((k) => {
      const sc = getScopes(k);
      return sc.includes('acm:admin') || sc.includes('*:read') || sc.includes('*:write') ||
        sc.some((s) => s.startsWith(mod + ':'));
    });
  });
}

// ── Module card ───────────────────────────────────────────────────────────────

function ModuleCard({ mod, namespaces }: { mod: ModuleDef; namespaces: Namespace[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const colors = MODULE_COLORS[mod.color] ?? MODULE_COLORS.slate;
  const keys = keysForModule(namespaces, mod.name);
  const readKeys = keys.filter((k) => {
    const sc = getScopes(k);
    return sc.includes('acm:admin') || sc.includes('*:read') || sc.includes('*:write') ||
      sc.includes(mod.readScope);
  });
  const writeKeys = keys.filter((k) => {
    const sc = getScopes(k);
    return sc.includes('acm:admin') || sc.includes('*:write') || sc.includes(mod.writeScope);
  });

  const readEps = mod.endpoints.filter((e) => e.scope.endsWith(':read'));
  const writeEps = mod.endpoints.filter((e) => e.scope.endsWith(':write'));

  return (
    <div className={`border ${colors.border} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className='w-full flex items-center gap-3 px-4 py-3.5 bg-gray-900/40 hover:bg-gray-800/30 transition-colors text-left'
      >
        {open
          ? <ChevronDown size={13} className='text-gray-500 shrink-0' />
          : <ChevronRight size={13} className='text-gray-500 shrink-0' />}
        <span className={`font-mono font-bold text-sm ${colors.text}`}>{mod.name}</span>
        <span className='text-xs text-gray-500'>{t(mod.descKey, mod.desc)}</span>
        <div className='flex items-center gap-2 ml-auto shrink-0'>
          {readEps.length > 0 && (
            <span className='flex items-center gap-1 text-[10px] text-blue-400'>
              <Eye size={9} />
              {readEps.length}
            </span>
          )}
          {writeEps.length > 0 && (
            <span className='flex items-center gap-1 text-[10px] text-orange-400'>
              <Pencil size={9} />
              {writeEps.length}
            </span>
          )}
          {keys.length > 0
            ? (
              <span className='flex items-center gap-1 text-[10px] text-green-400'>
                <Key size={9} />
                {t('apiMap.keyCount', '{{count}} klíčů', { count: keys.length })}
              </span>
            )
            : (
              <span className='flex items-center gap-1 text-[10px] text-gray-600'>
                <Key size={9} />
                {t('apiMap.noKey', 'žádný klíč')}
              </span>
            )}
        </div>
      </button>

      {open && (
        <div className={`${colors.bg} border-t ${colors.border}`}>
          {/* Key summary */}
          <div className='px-4 py-3 border-b border-gray-800/50 flex items-center justify-between'>
            <div className='flex items-center gap-4 text-xs text-gray-500'>
              <span className='flex items-center gap-1.5'>
                <Eye size={11} className='text-blue-400' />
                <span className='text-blue-400'>{readKeys.length}</span>{' '}
                {t('apiMap.readWord', 'read')}
              </span>
              <span className='flex items-center gap-1.5'>
                <Pencil size={11} className='text-orange-400' />
                <span className='text-orange-400'>{writeKeys.length}</span>{' '}
                {t('apiMap.writeWord', 'write')}
              </span>
              <span className='text-gray-600 font-mono'>{mod.readScope} · {mod.writeScope}</span>
            </div>
            <button
              onClick={() => navigate('/api')}
              className='flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors'
            >
              <Plus size={11} />
              {t('apiMap.createKey', 'Vytvořit klíč')}
            </button>
          </div>

          {/* Endpoint table */}
          <table className='w-full text-xs'>
            <thead>
              <tr className='border-b border-gray-800/40'>
                <th className='text-left px-4 py-2 text-gray-600 font-medium w-16'>
                  {t('apiMap.colMethod', 'Metoda')}
                </th>
                <th className='text-left px-4 py-2 text-gray-600 font-medium'>
                  {t('apiMap.colPath', 'Cesta')}
                </th>
                <th className='text-left px-4 py-2 text-gray-600 font-medium w-32'>
                  {t('apiMap.colScope', 'Scope')}
                </th>
                <th className='text-left px-4 py-2 text-gray-600 font-medium'>
                  {t('apiMap.colDesc', 'Popis')}
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800/30'>
              {mod.endpoints.map((ep, i) => (
                <tr key={i} className='hover:bg-gray-800/20 transition-colors'>
                  <td className='px-4 py-2'>
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-mono font-bold ${
                        METHOD_COLORS[ep.method] ?? ''
                      }`}
                    >
                      {ep.method}
                    </span>
                  </td>
                  <td className='px-4 py-2'>
                    <code className='text-gray-300 font-mono'>{mod.baseUrl}{ep.path}</code>
                  </td>
                  <td className='px-4 py-2'>
                    {ep.scope === 'webhook'
                      ? (
                        <span className='flex items-center gap-1 text-yellow-400'>
                          <Webhook size={9} />
                          {t('apiMap.webhookWord', 'webhook')}
                        </span>
                      )
                      : ep.scope.endsWith(':write')
                      ? (
                        <span className='flex items-center gap-1 text-orange-400'>
                          <Pencil size={9} />
                          {ep.scope}
                        </span>
                      )
                      : (
                        <span className='flex items-center gap-1 text-blue-400'>
                          <Eye size={9} />
                          {ep.scope}
                        </span>
                      )}
                  </td>
                  <td className='px-4 py-2 text-gray-500'>{t(ep.descKey, ep.desc)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Active keys list */}
          {keys.length > 0 && (
            <div className='px-4 py-3 border-t border-gray-800/50'>
              <div className='text-[10px] text-gray-600 mb-2 uppercase tracking-wide'>
                {t('apiMap.activeKeysLabel', 'Aktivní klíče s přístupem')}
              </div>
              <div className='flex flex-wrap gap-1.5'>
                {keys.map((k) => (
                  <span
                    key={k.id}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium ${colors.badge}`}
                  >
                    {k.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function ApiMap() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['acm-keys'],
    queryFn: () =>
      api.mcp<{ ok: boolean; namespaces: Namespace[] }>('identity.api_key_list', {}),
  });
  const namespaces = Array.isArray(data?.namespaces) ? data!.namespaces! : [];

  const q = search.toLowerCase().trim();
  const filtered = q
    ? MODULES.filter((m) =>
      m.name.includes(q) || t(m.descKey, m.desc).toLowerCase().includes(q) ||
      m.endpoints.some((e) =>
        e.path.includes(q) || t(e.descKey, e.desc).toLowerCase().includes(q) ||
        e.scope.includes(q)
      )
    )
    : MODULES;

  const totalEndpoints = MODULES.reduce((s, m) => s + m.endpoints.length, 0);
  const writeEndpoints = MODULES.reduce(
    (s, m) => s + m.endpoints.filter((e) => e.scope.endsWith(':write')).length,
    0,
  );
  const modulesWithKeys =
    MODULES.filter((m) => keysForModule(namespaces, m.name).length > 0).length;

  return (
    <div className='space-y-6 p-6'>
      <PageHeader
        title={t('apiMap.pageTitle', 'API přehled')}
        description={t(
          'apiMap.pageDescription',
          '{{modules}} modulů · {{endpoints}} endpointů · {{write}} write',
          { modules: MODULES.length, endpoints: totalEndpoints, write: writeEndpoints },
        )}
        icon={Network}
      />

      <StatsGrid>
        <StatCard label={t('apiMap.statModules', 'Modulů')} value={MODULES.length} />
        <StatCard
          label={t('apiMap.statEndpoints', 'Endpointů')}
          value={totalEndpoints}
          variant='info'
        />
        <StatCard
          label={t('apiMap.statWriteOps', 'Write ops')}
          value={writeEndpoints}
          variant='warning'
        />
        <StatCard
          label={t('apiMap.statCoveredByKey', 'Kryto klíčem')}
          value={modulesWithKeys}
          variant='success'
        />
      </StatsGrid>

      <div className='max-w-md'>
        <SearchInput
          value={search}
          onChange={setSearch}
          size='md'
          placeholder={t('apiMap.searchPlaceholder', 'Hledat modul, endpoint, scope…')}
          className='w-full'
        />
      </div>

      <div className='space-y-2'>
        {filtered.map((mod) => <ModuleCard key={mod.name} mod={mod} namespaces={namespaces} />)}
      </div>

      <div className='rounded-xl border border-gray-800 bg-gray-900/20 p-4'>
        <div className='flex items-center gap-2 mb-3 text-xs text-gray-400 font-medium'>
          <ShieldCheck size={12} className='text-blue-400' />{' '}
          {t('apiMap.authSectionTitle', 'Auth — jak posílat klíč')}
        </div>
        <div className='space-y-1.5 text-xs text-gray-500'>
          <div>
            <span className='text-gray-400 font-mono'>X-Api-Key: adx_…</span>{' '}
            — {t('apiMap.authHeaderDesc', 'hlavička pro API klienty a agenty')}
          </div>
          <div>
            <span className='text-gray-400 font-mono'>Authorization: Bearer adx_…</span>{' '}
            — {t('apiMap.authBearerDesc', 'alternativně Bearer token')}
          </div>
          <div className='pt-1 text-gray-600'>
            {t('apiMap.masterKeyDesc1', 'Master klíč z')}{' '}
            <code className='text-gray-500'>.env API_KEY</code>{' '}
            {t('apiMap.masterKeyDesc2', 'má scope')}{' '}
            <code className='text-gray-500'>acm:admin</code>{' '}
            {t('apiMap.masterKeyDesc3', 'a přístup ke všemu.')}
          </div>
        </div>
      </div>
    </div>
  );
}
